<script setup lang="ts" generic="T extends Record<string, any>">
import { CLASS_MARKER, DragItemContextInjectKey, useDragItemContext, useDragZoneContext } from './context'
import { useDragAndDropStore } from '~~/src/stores/drag-and-drop'
import { generateTransparentImage } from '@/utils/drag-and-drop'

interface Props {
  dataIndex?: number
  data: T
  is?: string
  idKey?: keyof T
  disabled?: boolean
  textContent?: string
}

const props = defineProps<Props>()
const zoneContext = useDragZoneContext()
const dragContext = useDragItemContext()

const generateDragImage = (event: DragEvent, imageClass: string, node?: HTMLElement) => {
  if (!node || !event.dataTransfer)
    return null
  const dragImageElement = node?.querySelector(`.${imageClass}`) ?? node
  const clone = dragImageElement.cloneNode(true) as HTMLDivElement
  document.body.appendChild(clone)
  clone.style.position = 'absolute'
  clone.style.top = '-1000'
  clone.style.left = '-1000'
  clone.style.zIndex = '-100000'
  const rect = clone.getBoundingClientRect()

  event.dataTransfer.setDragImage(clone,
    rect.width / 2,
    rect.height / 2,

  )
  clone.setAttribute('data-drag', 'true')
  return clone
}

const container = ref<HTMLDivElement | null>(null)
const dragged = ref(false)
const displayStyle = ref('')
const dragImageNode = ref<HTMLDivElement | null>(null)

const { setIsDragging } = useDragAndDropStore()

const id = props.idKey ? props.data[props.idKey] : randomID()

const isDraggable = ref(false)

function setData(e: DragEvent) {
  if (e.dataTransfer) {
    const index = props.dataIndex ?? -1
    e.dataTransfer.setData('text/json', JSON.stringify({ zoneId: zoneContext?.id, index, data: props.data }))
    if (props.textContent)
      e.dataTransfer.setData('text/plain', props.textContent)

    e.dataTransfer?.setDragImage(generateTransparentImage(), 0, 0)
    e.dataTransfer.effectAllowed = 'copy'
    e.dataTransfer.dropEffect = 'copy'
  }
}

const handlerDrag = (_: DragEvent) => {
  if (!container.value)
    return
  const item = container.value
  if (zoneContext?.isHover.value)
    item.style.display = 'none'
  else
    item.style.display = displayStyle.value

  zoneContext?.setChildrenDragged(props.dataIndex ?? -1)
}

const handlerDragEnd = (_: DragEvent) => {
  dragImageNode.value?.remove()
  isDraggable.value = false
  dragged.value = false
  setIsDragging(false, props.data)

  zoneContext?.removeChildrenDragged()
  if (container.value) {
    const item = container.value
    item.style.display = displayStyle.value
    item.classList.remove(CLASS_MARKER.DRAGGED)
  }
}

const handlerDragStart = (e: DragEvent) => {
  e.stopPropagation()
  if (!container.value)
    return
  const item = container.value
  item.classList.add(CLASS_MARKER.DRAGGED)
  setData(e)
  dragImageNode.value = generateDragImage(e, 'drag-image', item)
  dragged.value = true
  setIsDragging(true, props.data)
}

const handlerMouseDown = (e: MouseEvent) => {
  e.stopPropagation()
  if (e.button === 0)
    isDraggable.value = true
}

const handlerMouseUp = (_: MouseEvent) => {
  isDraggable.value = false
}

const isDisabled = computed(() => {
  return zoneContext?.disabled.value || props.disabled || !!dragContext?.isDragged.value
})

provide(DragItemContextInjectKey, {
  data: props.data,
  index: props.dataIndex ?? -1,
  disabled: isDisabled,
  isDragged: dragged,
  toggleDraggable: (value: boolean) => {
    isDraggable.value = value
  },
})

defineExpose({
  index: props.dataIndex,
  isDragged: dragged,
})
</script>

<template>
  <component
    :is="is ?? 'div'" ref="container" class="dragable select-none [&:first-child]:cursor-grab]"
    :data-id="id"
    data-index="index" :draggable="isDraggable" @dragstart="handlerDragStart($event)"
    @dragend="handlerDragEnd($event)" @drag="handlerDrag($event)" @mousedown="handlerMouseDown($event)"
    @mouseup="handlerMouseUp($event)"
  >
    <slot />
  </component>
</template>
